import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CardTemplateField {
  id: string;
  template_id: string;
  field_key: string;
  label: string;
  field_type: 'text' | 'date' | 'select' | 'qrcode';
  placeholder: string | null;
  max_length: number | null;
  is_required: boolean;
  options: string[] | null;
  position_side: 'front' | 'back';
  position_x: number;
  position_y: number;
  position_width: number;
  position_height: number;
  rotation: number;
  font_family: string | null;
  font_size: string | null;
  font_color: string | null;
  font_weight: string | null;
  text_transform: string | null;
  text_align: 'left' | 'center' | 'right' | null;
  display_order: number;
  linked_to_field_key: string | null;
  is_locked: boolean;
}

export interface CardTemplate {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price: number;
  compare_at_price: number | null;
  category: 'license' | 'id-card' | 'specialty';
  badge: 'bestseller' | 'new' | 'sale' | null;
  front_image_url: string;
  back_image_url: string | null;
  thumbnail_front_url: string | null;
  thumbnail_back_url: string | null;
  hero_image_url: string | null;
  animation_url: string | null;
  showcase_image_url: string | null;
  gallery_images: string[];
  photo_position: { x: number; y: number; width: number; height: number; rotation?: number };
  photo_side: 'front' | 'back';
  signature_position: { x: number; y: number; width: number; height: number; rotation?: number };
  signature_side: 'front' | 'back';
  cart_name: string | null;
  is_active: boolean;
  display_order: number;
  fields?: CardTemplateField[];
}

// Fetch all active templates (public)
export function useCardTemplates() {
  return useQuery({
    queryKey: ['card-templates'],
    queryFn: async (): Promise<CardTemplate[]> => {
      const { data: templates, error } = await supabase
        .from('card_templates')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;

      // Fetch fields for all templates
      const templateIds = templates.map(t => t.id);
      const { data: fields, error: fieldsError } = await supabase
        .from('card_template_fields')
        .select('*')
        .in('template_id', templateIds)
        .order('display_order', { ascending: true });

      if (fieldsError) throw fieldsError;

      // Map fields to templates
      return templates.map(template => ({
        ...template,
        photo_position: template.photo_position as CardTemplate['photo_position'],
        signature_position: template.signature_position as CardTemplate['signature_position'],
        gallery_images: (template.gallery_images as string[]) || [],
        fields: fields?.filter(f => f.template_id === template.id) as CardTemplateField[] || []
      }));
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

// Fetch single template by slug (public)
export function useCardTemplate(slug: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['card-template', slug],
    queryFn: async (): Promise<CardTemplate | null> => {
      const { data: template, error } = await supabase
        .from('card_templates')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .maybeSingle();

      if (error) throw error;
      if (!template) return null;

      // Fetch fields for this template
      const { data: fields, error: fieldsError } = await supabase
        .from('card_template_fields')
        .select('*')
        .eq('template_id', template.id)
        .order('display_order', { ascending: true });

      if (fieldsError) throw fieldsError;

      return {
        ...template,
        photo_position: template.photo_position as CardTemplate['photo_position'],
        signature_position: template.signature_position as CardTemplate['signature_position'],
        gallery_images: (template.gallery_images as string[]) || [],
        fields: fields as CardTemplateField[] || []
      };
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
    // Use cached list data as instant placeholder to avoid skeleton flash
    initialData: () => {
      const allTemplates = queryClient.getQueryData<CardTemplate[]>(['card-templates']);
      return allTemplates?.find(t => t.slug === slug) ?? undefined;
    },
    initialDataUpdatedAt: () => {
      return queryClient.getQueryState(['card-templates'])?.dataUpdatedAt;
    },
  });
}

// Admin: Fetch all templates (including inactive)
export function useAdminCardTemplates() {
  return useQuery({
    queryKey: ['admin-card-templates'],
    queryFn: async (): Promise<CardTemplate[]> => {
      const { data: templates, error } = await supabase
        .from('card_templates')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;

      const templateIds = templates.map(t => t.id);
      const { data: fields, error: fieldsError } = await supabase
        .from('card_template_fields')
        .select('*')
        .in('template_id', templateIds)
        .order('display_order', { ascending: true });

      if (fieldsError) throw fieldsError;

      return templates.map(template => ({
        ...template,
        photo_position: template.photo_position as CardTemplate['photo_position'],
        signature_position: template.signature_position as CardTemplate['signature_position'],
        gallery_images: (template.gallery_images as string[]) || [],
        fields: fields?.filter(f => f.template_id === template.id) as CardTemplateField[] || []
      }));
    },
  });
}

// Admin: Create template
export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (template: Omit<CardTemplate, 'id' | 'fields'>) => {
      const { data, error } = await supabase
        .from('card_templates')
        .insert({
          ...template,
          photo_position: template.photo_position,
          signature_position: template.signature_position,
          gallery_images: template.gallery_images,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-card-templates'] });
      queryClient.invalidateQueries({ queryKey: ['card-templates'] });
    },
  });
}

// Admin: Update template
export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...template }: Partial<CardTemplate> & { id: string }) => {
      const { data, error } = await supabase
        .from('card_templates')
        .update({
          ...template,
          photo_position: template.photo_position,
          signature_position: template.signature_position,
          gallery_images: template.gallery_images,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-card-templates'] });
      queryClient.invalidateQueries({ queryKey: ['card-templates'] });
    },
  });
}

// Admin: Delete template
export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('card_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-card-templates'] });
      queryClient.invalidateQueries({ queryKey: ['card-templates'] });
    },
  });
}

// Admin: Create/Update field
export function useUpsertTemplateField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (field: Omit<CardTemplateField, 'id'> & { id?: string }) => {
      if (field.id) {
        const { data, error } = await supabase
          .from('card_template_fields')
          .update(field)
          .eq('id', field.id)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('card_template_fields')
          .insert(field)
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-card-templates'] });
      queryClient.invalidateQueries({ queryKey: ['card-templates'] });
    },
  });
}

// Admin: Delete field
export function useDeleteTemplateField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('card_template_fields')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-card-templates'] });
      queryClient.invalidateQueries({ queryKey: ['card-templates'] });
    },
  });
}
